import React from 'react';
import { Link } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'motion/react';
import { NavDestination } from '../../config/navigation/navigation';
import { MdDashboard, MdPeople, MdDescription, MdAssignment, MdBarChart, MdExpandMore } from 'react-icons/md';
import { dashboardSprings, dashboardTransitions } from 'lib/motion/dashboardMotion';

const NAV_ICONS: Record<string, (props: { className?: string }) => React.ReactNode> = {
  dashboard: MdDashboard,
  users: MdPeople,
  template: MdDescription,
  document: MdAssignment,
  chart: MdBarChart,
};

const ICON_COLORS: Record<string, string> = {
  dashboard: 'text-white/85',
  users: 'text-white/85',
  template: 'text-white/85',
  document: 'text-white/85',
  chart: 'text-white/85',
};

interface NavigationItemProps {
  item: NavDestination;
  isActive: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  onToggle?: () => void;
  level?: number;
  isCollapsed: boolean;
  children?: React.ReactNode;
}

const NavigationItem: React.FC<NavigationItemProps> = ({
  item,
  isActive,
  isExpanded,
  hasChildren,
  onToggle,
  level = 0,
  isCollapsed,
  children,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const rotateX = useSpring(useTransform(pointerY, [-0.5, 0.5], [1.2, -1.2]), dashboardSprings.micro);
  const rotateY = useSpring(useTransform(pointerX, [-0.5, 0.5], [-1.4, 1.4]), dashboardSprings.micro);
  const liftY = useSpring(useTransform(pointerY, [-0.5, 0.5], [0.5, -0.5]), dashboardSprings.micro);
  const shadowAlpha = useTransform(pointerY, [-0.5, 0.5], [0.06, 0.15]);
  const shadow = useMotionTemplate`0 8px 24px rgba(15,23,42,${shadowAlpha})`;

  const baseClasses = `
    relative flex items-center px-3 py-2 text-sm font-medium rounded-md
    ${level > 0 ? 'ml-4' : ''}
  `;

  const activeClasses = isActive ? 'text-white' : 'text-white/80 hover:text-white';

  const Icon = item.icon ? NAV_ICONS[item.icon] : null;
  const iconColorClass = item.icon ? (ICON_COLORS[item.icon] ?? 'text-gray-500') : 'text-gray-500';

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (prefersReducedMotion) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    pointerX.set(x);
    pointerY.set(y);
  };

  const resetPointer = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  const content = (
    <motion.div
      layout
      className={`${baseClasses} ${activeClasses} w-full ${isCollapsed ? 'justify-center px-2' : ''}`}
      title={isCollapsed ? item.label : undefined}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
      onFocus={resetPointer}
      style={
        prefersReducedMotion
          ? undefined
          : {
              rotateX,
              rotateY,
              y: liftY,
              transformPerspective: 900,
              boxShadow: shadow,
            }
      }
      whileHover={prefersReducedMotion ? undefined : { backgroundColor: 'rgba(255, 255, 255, 0.10)' }}
      whileTap={prefersReducedMotion ? undefined : { scale: 0.99 }}
      transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
    >
      {isActive && (
        <motion.div
          layoutId={isCollapsed ? 'sidebar-active-pill-collapsed' : 'sidebar-active-pill'}
          className="absolute inset-0 rounded-md border border-white/35 bg-white/15"
          transition={prefersReducedMotion ? { duration: 0 } : dashboardSprings.secondary}
        />
      )}

      {Icon && (
        <span className={`relative z-[1] text-lg ${iconColorClass} ${isCollapsed ? '' : 'mr-3'}`}>
          {Icon({ className: 'w-5 h-5' })}
        </span>
      )}

      {!isCollapsed && (
        <>
          <span className="relative z-[1] flex-1">{item.label}</span>

          {item.badge && (
            <span className="relative z-[1] ml-2 rounded-full bg-proposio-purple px-2 py-1 text-xs text-white">
              {item.badge}
            </span>
          )}

          {hasChildren && (
            <motion.span
              className="relative z-[1] ml-2"
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
            >
              {MdExpandMore({ className: 'h-4 w-4' })}
            </motion.span>
          )}
        </>
      )}
    </motion.div>
  );

  if (hasChildren) {
    return (
      <motion.div layout transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.layoutSoft}>
        <button onClick={onToggle} className="w-full text-left">
          {content}
        </button>

        <AnimatePresence initial={false}>
          {isExpanded && !isCollapsed && (
            <motion.div
              key={`${item.id}-children`}
              layout
              className="mt-1 space-y-1"
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.layoutSoft}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <motion.div layout transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.layoutSoft}>
      <Link to={item.path} className="block">
        {content}
      </Link>
    </motion.div>
  );
};

export default NavigationItem;
